import { getWebInitTimestamp } from "./time";
import { CUJStatus, UserDropReason } from "./types";
class StabilityTrackingModule {
  resetForSPA() {
    this._hasReportedPageReady = false;
    this._hasReportedPageCrash = false;
    this._hasReportedUserDrop = false;
    this._pageReadyTimestamp = -1;
    this._cuj = {};
  }
  constructor(trackingContext) {
    this._hasReportedPageReady = false;
    this._hasReportedPageCrash = false;
    this._hasReportedUserDrop = false;
    this._pageReadyTimestamp = -1;
    this._cuj = {};
    this.reportPageReady = () => {
      if (this._hasReportedPageReady || this._hasReportedUserDrop || this._hasReportedPageCrash) {
        return;
      }
      this._hasReportedPageReady = true;
      let duration = 0;
      try {
        performance.mark("pageReady");
        duration = performance.measure("navigationStart_to_pageReady", "navigationStart", "pageReady").duration;
      } catch (e) {
        duration = Date.now() - getWebInitTimestamp();
      }
      this._pageReadyTimestamp = duration;
      this._trackingContext.sendEvent({
        name: "page_ready",
        metrics: {
          time: duration
        }
      });
    };
    this.reportUserDrop = () => {
      if (this._hasReportedPageReady || this._hasReportedUserDrop) {
        return;
      }
      this._hasReportedUserDrop = true;
      let duration = 0;
      try {
        performance.mark("userDrop");
        duration = performance.measure("navigationStart_to_userDrop", "navigationStart", "userDrop").duration;
      } catch (e) {
        duration = Date.now() - getWebInitTimestamp();
      }
      this._trackingContext.sendEvent({
        name: "user_drop",
        metrics: {
          time: duration
        },
        categories: {
          reason: this._hasReportedPageCrash ? UserDropReason.CRASH : UserDropReason.ABORT
        }
      });
    };
    this.reportPageCrash = (error, hasDifferentTriggerTime = false, componentStack) => {
      var _message_substring, _stack_substring, _componentStack_substring;
      if (this._hasReportedUserDrop || this._hasReportedPageCrash) {
        return;
      }
      this._hasReportedPageCrash = true;
      const { name = "", message, stack, _triggerTime = 0 } = error !== null && error !== void 0 ? error : {};
      let reportTime = 0;
      if (!reportTime) {
        try {
          performance.mark("pageCrashReporting");
          reportTime = performance.measure("navigationStart_to_pageCrashReporting", "navigationStart", "pageCrashReporting").duration;
        } catch (e) {
          reportTime = Date.now() - getWebInitTimestamp();
        }
      }
      const triggerTime = hasDifferentTriggerTime ? _triggerTime : reportTime;
      const isTriggeredBeforePageReady = triggerTime < this._pageReadyTimestamp || !this._hasReportedPageReady;
      const isReportedBeforePageReady = reportTime < this._pageReadyTimestamp || !this._hasReportedPageReady;
      var _message_substring1, _stack_substring1, _componentStack_substring1;
      this._trackingContext.sendEvent({
        name: "page_crash",
        metrics: {
          time: triggerTime !== null && triggerTime !== void 0 ? triggerTime : 0
        },
        categories: {
          isTriggeredBeforePageReady,
          isReportedBeforePageReady,
          name,
          // slardar max string length 256 chars
          message: (_message_substring1 = message === null || message === void 0 ? void 0 : (_message_substring = message.substring) === null || _message_substring === void 0 ? void 0 : _message_substring.call(message, 0, 256)) !== null && _message_substring1 !== void 0 ? _message_substring1 : "",
          stack: (_stack_substring1 = stack === null || stack === void 0 ? void 0 : (_stack_substring = stack.substring) === null || _stack_substring === void 0 ? void 0 : _stack_substring.call(stack, 0, 256)) !== null && _stack_substring1 !== void 0 ? _stack_substring1 : "",
          componentStack: (_componentStack_substring1 = componentStack === null || componentStack === void 0 ? void 0 : (_componentStack_substring = componentStack.substring) === null || _componentStack_substring === void 0 ? void 0 : _componentStack_substring.call(componentStack, 0, 256)) !== null && _componentStack_substring1 !== void 0 ? _componentStack_substring1 : ""
        }
      });
      if (error) {
        this._trackingContext.sendPageCrashError(error, {
          name: "page_crash",
          isTriggeredBeforePageReady: "" + isTriggeredBeforePageReady,
          isReportedBeforePageReady: "" + isReportedBeforePageReady
        });
      }
    };
    this.reportCUJ = (name, status, extra) => {
      var _errorInfo_substring, _detailStr_substring;
      const cujStartMetricsName = `cuj_${name}_${CUJStatus.START}`;
      const currentMetricsName = `cuj_${name}_${status}`.replace(/\s/g, "_");
      let durationFromNavStart = 0;
      let durationFromCUJStart = 0;
      try {
        performance.mark(currentMetricsName);
        durationFromNavStart = performance.measure(`navigationStart_to_${currentMetricsName}`, "navigationStart", currentMetricsName).duration;
        durationFromCUJStart = performance.measure(`${cujStartMetricsName}_to_${currentMetricsName}`, cujStartMetricsName, currentMetricsName).duration;
      } catch (e) {
        durationFromNavStart = Date.now() - getWebInitTimestamp();
        if (status === CUJStatus.START) {
          this._cuj[name] = {
            [CUJStatus.START]: Date.now()
          };
          durationFromCUJStart = 0;
        } else {
          if (!this._cuj[name]) {
            return;
          }
          this._cuj[name][status] = Date.now();
          var _this__cuj_name_CUJStatus_START;
          durationFromCUJStart = this._cuj[name][status] - ((_this__cuj_name_CUJStatus_START = this._cuj[name][CUJStatus.START]) !== null && _this__cuj_name_CUJStatus_START !== void 0 ? _this__cuj_name_CUJStatus_START : 0);
        }
      }
      const { error, detail, stepName } = extra !== null && extra !== void 0 ? extra : {};
      var _error_toString;
      const errorInfo = typeof error === "string" ? error : (_error_toString = error === null || error === void 0 ? void 0 : error.toString()) !== null && _error_toString !== void 0 ? _error_toString : "";
      var _error_stack;
      const detailStr = detail ? detail : (_error_stack = error === null || error === void 0 ? void 0 : error.stack) !== null && _error_stack !== void 0 ? _error_stack : "";
      var _errorInfo_substring1, _detailStr_substring1;
      this._trackingContext.sendEvent({
        name: "pns_cuj",
        metrics: {
          timestamp: durationFromNavStart,
          duration: durationFromCUJStart
        },
        categories: {
          status,
          name,
          stepName,
          // slardar max string length 256 chars
          error: (_errorInfo_substring1 = errorInfo === null || errorInfo === void 0 ? void 0 : (_errorInfo_substring = errorInfo.substring) === null || _errorInfo_substring === void 0 ? void 0 : _errorInfo_substring.call(errorInfo, 0, 256)) !== null && _errorInfo_substring1 !== void 0 ? _errorInfo_substring1 : "",
          detail: (_detailStr_substring1 = detailStr === null || detailStr === void 0 ? void 0 : (_detailStr_substring = detailStr.substring) === null || _detailStr_substring === void 0 ? void 0 : _detailStr_substring.call(detailStr, 0, 256)) !== null && _detailStr_substring1 !== void 0 ? _detailStr_substring1 : ""
        }
      });
      if (status === CUJStatus.FAILURE && error) {
        this._trackingContext.sendCUJError(error, {
          stepName,
          cujName: name,
          status,
          name: "pns_cuj"
        });
      }
    };
    this._trackingContext = trackingContext;
  }
}
export {
  StabilityTrackingModule
};
